import React, { useState, useRef, useEffect } from "react";
import "../../styles/Part1Tutorial.css";
import { shuffledGrammarPatterns } from "../../data/part1/grammar/grammarPatterns";
import { db } from "../../firebase";
import { doc, updateDoc, getDoc, setDoc } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { FaStar } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const Part1Tutorial = ({ onBack }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [hasListened, setHasListened] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const audioRef = useRef(null);
  const [starredQuestions, setStarredQuestions] = useState([]);
  const [wrongQuestions, setWrongQuestions] = useState([]);
  const [mode, setMode] = useState("all"); // 'all' 또는 'wrong'
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [autoRemoveWrongQuestions, setAutoRemoveWrongQuestions] =
    useState(true);

  // 틀린 문제 불러오기
  useEffect(() => {
    const loadWrongQuestions = async () => {
      if (currentUser) {
        const userDoc = doc(
          db,
          "users",
          currentUser.uid,
          "part1",
          "grammarPractice"
        );
        const docSnap = await getDoc(userDoc);
        if (docSnap.exists()) {
          setWrongQuestions(docSnap.data().wrongQuestions || []);
        }
      }
    };
    loadWrongQuestions();
  }, [currentUser]);

  // 별표 문제 불러오기
  useEffect(() => {
    const loadStarredQuestions = async () => {
      if (currentUser) {
        const userDoc = doc(db, "users", currentUser.uid);
        const docSnap = await getDoc(userDoc);
        if (docSnap.exists()) {
          setStarredQuestions(docSnap.data().starredQuestions || []);
        }
      }
    };
    loadStarredQuestions();
  }, [currentUser]);

  // 현재 보여줄 문제 필터링
  const filteredQuestions =
    mode === "wrong" && wrongQuestions.length > 0
      ? shuffledGrammarPatterns.filter((q) =>
          wrongQuestions.includes(q.questionNumber)
        )
      : shuffledGrammarPatterns;

  // 필터링된 문제가 없으면 모드를 'all'로 전환
  useEffect(() => {
    if (mode === "wrong" && filteredQuestions.length === 0) {
      setMode("all");
    }
  }, [filteredQuestions.length, mode]);

  // 현재 문제 가져오기
  const currentQuestion = filteredQuestions[currentSlide];

  // 이제 currentQuestion이 정의된 후에 useEffect 사용
  useEffect(() => {
    if (audioRef.current && currentQuestion) {
      const audioPath = `/audios/part1/grammar/sentence${currentQuestion.questionNumber}.mp3`;
      console.log("오디오 파일 경로 설정:", audioPath);
      console.log("현재 문제:", currentQuestion.question);
      audioRef.current.src = audioPath;
      audioRef.current.load();
    }
  }, [currentSlide, currentQuestion]);

  // 답 선택 처리
  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  // 정답 확인 처리
  const checkAnswer = async () => {
    const isAnswerCorrect = selectedAnswer === currentQuestion.answer;
    setIsCorrect(isAnswerCorrect);
    setShowAnswer(true);

    if (currentUser) {
      const userDoc = doc(
        db,
        "users",
        currentUser.uid,
        "part1",
        "grammarPractice"
      );
      console.log(
        "Firebase 경로:",
        `users/${currentUser.uid}/part1/grammarPractice`
      );

      try {
        let newWrongQuestions;
        if (!isAnswerCorrect) {
          newWrongQuestions = [
            ...new Set([...wrongQuestions, currentQuestion.questionNumber]),
          ];
          console.log(
            `문제 ${currentQuestion.questionNumber}번이 틀린 문제 목록에 추가됨`
          );
          console.log("현재 틀린 문제 목록:", newWrongQuestions);
        } else {
          if (autoRemoveWrongQuestions) {
            newWrongQuestions = wrongQuestions.filter(
              (num) => num !== currentQuestion.questionNumber
            );
          } else {
            newWrongQuestions = [...wrongQuestions];
          }
          console.log(
            `문제 ${currentQuestion.questionNumber}번이 틀린 문제 목록에서 제거됨`
          );
          console.log("현재 틀린 문제 목록:", newWrongQuestions);

          // 틀린 문제가 모두 해결되었는지 확인
          if (mode === "wrong" && newWrongQuestions.length === 0) {
            setMode("all");
            setCurrentSlide(0);
            alert(
              "모든 틀린 문제를 해결했습니다! 전체 문제 풀기 모드로 전환됩니다."
            );
          }
        }

        setWrongQuestions(newWrongQuestions);

        const docSnap = await getDoc(userDoc);
        if (!docSnap.exists()) {
          await setDoc(userDoc, {
            wrongQuestions: newWrongQuestions,
          });
          console.log(
            "새로운 문서 생성됨. 저장된 틀린 문제:",
            newWrongQuestions
          );
        } else {
          await updateDoc(userDoc, {
            wrongQuestions: newWrongQuestions,
          });
          console.log(
            "기존 문서 업데이트됨. 저장된 틀린 문제:",
            newWrongQuestions
          );
        }
      } catch (error) {
        console.error("Firebase 업데이트 중 오류 발생:", error);
      }
    }
  };

  // mode가 변경될 때 상태 초기화
  useEffect(() => {
    setCurrentSlide(0);
    setShowAnswer(false);
    setHasListened(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
  }, [mode]);

  // 다음 문제로 이동할 때 상태 초기화
  const handleNextSlide = () => {
    setCurrentSlide((prev) =>
      prev === filteredQuestions.length - 1 ? 0 : prev + 1
    );
    setShowAnswer(false);
    setHasListened(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  // 이전 문제로 이동할 때 상태 초기화
  const handlePrevSlide = () => {
    setCurrentSlide((prev) =>
      prev === 0 ? filteredQuestions.length - 1 : prev - 1
    );
    setShowAnswer(false);
    setHasListened(false);
    setSelectedAnswer(null);
    setIsCorrect(null);
  };

  const handlePlayAudio = () => {
    // 음성 재생 시 정답과 선택한 답변 초기화
    setShowAnswer(false);
    setSelectedAnswer(null);
    setIsCorrect(null);

    const audio = audioRef.current;

    // 현재 보여지는 문제의 questionNumber 사용
    const audioPath = `/audios/part1/grammar/sentence${currentQuestion.questionNumber}.mp3`;
    console.log("재생 중인 오디오 문제 번호:", currentQuestion.questionNumber);
    console.log("현재 표시된 문제:", currentQuestion.question);

    if (!audio) {
      console.error("오디오 엘리먼트를 찾을 수 없습니다.");
      return;
    }

    // 항상 현재 문제에 맞는 오디오 설정
    audio.src = audioPath;
    audio.load();

    audio.play().catch((error) => {
      console.error("오디오 재생 중 오류 발생:", error);
    });

    setHasListened(false);

    audio.onended = () => {
      setHasListened(true);
    };
  };

  // 별표 토글 함수
  const toggleStar = async (questionNumber) => {
    if (!currentUser) return;

    // 즉시 UI 업데이트
    const newStarredQuestions = starredQuestions.includes(questionNumber)
      ? starredQuestions.filter((num) => num !== questionNumber)
      : [...starredQuestions, questionNumber];

    setStarredQuestions(newStarredQuestions); // 먼저 상태 업데이트

    try {
      const userDoc = doc(db, "users", currentUser.uid);
      const docSnap = await getDoc(userDoc);

      if (!docSnap.exists()) {
        await setDoc(userDoc, {
          starredQuestions: newStarredQuestions,
        });
      } else {
        await updateDoc(userDoc, {
          starredQuestions: newStarredQuestions,
        });
      }
    } catch (error) {
      // 에러 발생 시 원래 상태로 복구
      setStarredQuestions(starredQuestions);
      console.error("Error toggling star:", error);
    }
  };

  const handleBack = () => {
    navigate("/part1"); // Part1_1 컴포넌트로 이동
  };

  return (
    <div className="tutorial-container">
      <button
        className="back-button"
        onClick={onBack} // props로 받은 onBack 함수 사용
      >
        ← 학습 유형 선택으로 돌아가기
      </button>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2>Part 1 문법 학습</h2>
        <button
          className="back-button"
          style={{ backgroundColor: "#4a4a7a" }}
          onClick={() => setShowVideoModal(true)}
        >
          설명 동영상 보기
        </button>
      </div>

      <div className="grammar-container">
        <div className="mode-selector-container">
          <div className="mode-selector">
            <button
              className={`mode-button ${mode === "all" ? "active" : ""}`}
              onClick={() => setMode("all")}
            >
              전체 문제 풀기
            </button>
            <button
              className={`mode-button ${mode === "wrong" ? "active" : ""}`}
              onClick={() => setMode("wrong")}
              disabled={wrongQuestions.length === 0}
              style={{
                opacity: wrongQuestions.length === 0 ? 0.5 : 1,
                cursor: wrongQuestions.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              틀린 문제만 풀기{" "}
              {wrongQuestions.length > 0 && `(${wrongQuestions.length}개)`}
            </button>
          </div>
          <div className="wrong-questions-settings">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={autoRemoveWrongQuestions}
                onChange={() =>
                  setAutoRemoveWrongQuestions(!autoRemoveWrongQuestions)
                }
              />
              <span>맞춘 문제 자동 제거</span>
            </label>
          </div>
        </div>

        <div className="grammar-content">
          {currentQuestion ? (
            <>
              <div className="audio-section">
                <audio ref={audioRef} preload="auto">
                  <source src={currentQuestion.audioFile} type="audio/mpeg" />
                </audio>
                <button className="play-audio-btn" onClick={handlePlayAudio}>
                  음성 듣기
                </button>
              </div>

              {hasListened && (
                <div className="part1-question-section">
                  <div className="question-header">
                    <p className="korean-question">
                      {currentQuestion.question}
                    </p>
                  </div>

                  <div className="answer-buttons">
                    <button
                      className={`answer-button ${
                        selectedAnswer === "있음" ? "selected" : ""
                      }`}
                      onClick={() => handleAnswerSelect("있음")}
                    >
                      있음
                    </button>
                    <button
                      className={`answer-button ${
                        selectedAnswer === "없음" ? "selected" : ""
                      }`}
                      onClick={() => handleAnswerSelect("없음")}
                    >
                      없음
                    </button>
                  </div>

                  {selectedAnswer && !showAnswer && (
                    <button className="check-answer-btn" onClick={checkAnswer}>
                      정답 확인하기
                    </button>
                  )}
                </div>
              )}

              {showAnswer && (
                <div className="part1-explanation">
                  <div className="answer-header">
                    <p
                      className={`answer ${
                        isCorrect ? "correct" : "incorrect"
                      }`}
                    >
                      {isCorrect ? "정답입니다! " : "틀렸습니다. "}
                      정답: {currentQuestion.answer}
                    </p>
                  </div>
                  <p className="english-sentence">{currentQuestion.sentence}</p>
                  <p className="reason" style={{ whiteSpace: "pre-line" }}>
                    {currentQuestion.explanation
                      .replace(". ", ".\n")
                      .replace("(", "\n(")}
                  </p>
                  {currentQuestion.explanationImage && (
                    <div className="explanation-image-container">
                      <img
                        src={currentQuestion.explanationImage}
                        alt="문제 설명 이미지"
                        className="explanation-image"
                      />
                    </div>
                  )}
                </div>
              )}

              {showAnswer &&
                isCorrect &&
                mode === "wrong" &&
                !autoRemoveWrongQuestions && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: "12px",
                    }}
                  >
                    <button
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#3a3a3a",
                        color: "#e0e0e0",
                        border: "1px solid #555",
                        borderRadius: "4px",
                        fontSize: "14px",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onClick={() => {
                        const newWrongQuestions = wrongQuestions.filter(
                          (num) => num !== currentQuestion.questionNumber
                        );
                        setWrongQuestions(newWrongQuestions);
                        updateDoc(
                          doc(
                            db,
                            "users",
                            currentUser.uid,
                            "part1",
                            "grammarPractice"
                          ),
                          {
                            wrongQuestions: newWrongQuestions,
                          }
                        );
                      }}
                    >
                      틀린 문제 목록에서 제거하기
                    </button>
                  </div>
                )}

              <div className="navigation">
                <button onClick={handlePrevSlide}>이전</button>
                <span>
                  {currentSlide + 1} / {filteredQuestions.length}
                </span>
                <button onClick={handleNextSlide}>다음</button>
              </div>
            </>
          ) : (
            <div className="no-questions-message">
              현재 틀린 문제가 없습니다. 전체 문제 풀기 모드로 전환하세요.
            </div>
          )}
        </div>
      </div>

      {showVideoModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1100,
          }}
          onClick={() => setShowVideoModal(false)}
        >
          <div
            style={{
              background: "#222",
              borderRadius: "8px",
              padding: "20px",
              width: "90%",
              maxWidth: "800px",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                color: "white",
                fontSize: "24px",
                cursor: "pointer",
              }}
              onClick={() => setShowVideoModal(false)}
            >
              ×
            </button>
            <video
              src="/videos/part1-grammar-explanation.mp4"
              controls
              autoPlay
              style={{ width: "100%", marginTop: "20px" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Part1Tutorial;
